import type { ThreadMetaEntry } from 'features/libra/comments/commentsApi'
import type {
  IndexedUnit,
  TicketChain,
} from 'features/libra/dashboard/ticketDashboard/libra/data/indexTickets'
import { formatShortDate } from 'features/libra/dashboard/ticketDashboard/libra/formatShortDate'
import {
  creatorPill,
  versionPill,
} from 'features/libra/dashboard/ticketDashboard/libra/pills/basicPills'
import { commentsPill } from 'features/libra/dashboard/ticketDashboard/libra/pills/commentsPill'
import {
  fieldPills,
  fieldPillScope,
} from 'features/libra/dashboard/ticketDashboard/libra/pills/fieldPills'
import type { SignoffPills } from 'features/libra/dashboard/ticketDashboard/libra/pills/signoffPills'
import type { StagePills } from 'features/libra/dashboard/ticketDashboard/libra/pills/stagePills'
import type { TicketDashboardSettings } from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import { ticketLink } from 'features/libra/dashboard/ticketDashboard/libra/ticketLink'
import type {
  CardView,
  Pill,
  TicketView,
} from 'features/libra/dashboard/ticketDashboard/types'
import { computeWorkflowProgress } from 'features/libra/helpers/getWorkflowPercComplete'
import { isTerminalStage } from 'features/libra/lifecycle/lifecycleStageHelpers'
import type { SignoffsField } from 'features/libra/signoff/signoffTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import compact from 'lodash/compact'
import { displayNameFromEmail } from 'utils/stringHelpers'

export interface CardSources {
  units: ReadonlyArray<IndexedUnit>
  settings: TicketDashboardSettings
  stagePills: StagePills
  signoffPills: SignoffPills
  workflows: ReadonlyMap<string, LibraWorkflow>
  getUnitName: (unitId: string) => string | undefined
  getSiteName: (siteId: string) => string | undefined
  /** Keyed by ticket id. Omitted while loading, so no pill shows a misleading count. */
  signoffsByTicketId?: ReadonlyMap<string, SignoffsField>
  unresolvedThreadsByTicketId?: ReadonlyMap<
    string,
    ReadonlyArray<ThreadMetaEntry>
  >
  hasCreateVersionPermission?: boolean
}

const NO_SIGNOFFS: Readonly<SignoffsField> = {}
const NO_THREADS: ReadonlyArray<ThreadMetaEntry> = []

function ticketDate(ticket: LibraTicket): string | undefined {
  const date = ticket.updatedAt || ticket.createdAt
  return date ? formatShortDate(date) : undefined
}

function versionTitle(ticket: LibraTicket): string {
  return [
    ticket.createdBy && displayNameFromEmail(ticket.createdBy),
    ticketDate(ticket),
  ]
    .filter(Boolean)
    .join(' · ')
}

function headlineTicketView(
  ticket: LibraTicket,
  sources: CardSources
): TicketView {
  const {
    settings,
    stagePills,
    signoffPills,
    workflows,
    signoffsByTicketId,
    unresolvedThreadsByTicketId,
  } = sources
  const { builtIns } = settings
  const workflow = workflows.get(ticket.workflowId)
  const { pill: stage, terminal } = stagePills.resolve(ticket)
  const signoffStages = builtIns.signoffs.stages
  const showSignoffs =
    builtIns.signoffs.enabled &&
    (!signoffStages || signoffStages.includes(stage.value))
  const pills: Pill[] = compact([
    builtIns.stage.enabled ? stage : undefined,
    builtIns.version.enabled ? versionPill(ticket) : undefined,
    builtIns.creator.enabled ? creatorPill(ticket) : undefined,
    builtIns.comments.enabled && unresolvedThreadsByTicketId
      ? commentsPill(
          ticket,
          workflow,
          unresolvedThreadsByTicketId.get(ticket.id) ?? NO_THREADS
        )
      : undefined,
    ...settings.fields
      .filter((field) => fieldPillScope(field) === 'ticket')
      .flatMap((field) => fieldPills(field, ticket)),
    ...(showSignoffs && signoffsByTicketId
      ? signoffPills.forTicket(
          ticket,
          workflow,
          signoffsByTicketId.get(ticket.id) ?? NO_SIGNOFFS,
          stage.value
        )
      : []),
  ])

  return {
    id: ticket.id,
    title: workflow?.name ?? ticket.title,
    subtitle: ticketDate(ticket),
    pills,
    progress:
      workflow?.showTicketProgress !== false
        ? {
            percent: computeWorkflowProgress(ticket, workflow).percent,
            inReview: ticket.workflowComplete && !terminal,
            tone: stage.tone,
          }
        : undefined,
    link: ticketLink(ticket, 'Open ticket'),
    canCreateVersion:
      sources.hasCreateVersionPermission === true &&
      isTerminalStage(ticket, workflow?.lifecycleConfig),
  }
}

/** Same workflow as the headline, so the title is who and when rather than the workflow name. */
function earlierTicketView(
  ticket: LibraTicket,
  sources: CardSources
): TicketView {
  const { builtIns } = sources.settings
  return {
    id: ticket.id,
    title: versionTitle(ticket) || ticket.title,
    pills: compact([
      builtIns.stage.enabled
        ? sources.stagePills.resolve(ticket).pill
        : undefined,
      builtIns.version.enabled ? versionPill(ticket) : undefined,
    ]),
    link: ticketLink(ticket, 'Open ticket'),
  }
}

function chainToCard(
  unit: IndexedUnit,
  chain: TicketChain,
  assetPills: Pill[],
  sources: CardSources
): CardView | undefined {
  const [headline, ...earlier] = chain.versions
  if (!headline) return undefined
  const siteId = headline.tags.site
  return {
    id: chain.id,
    title: sources.getUnitName(unit.unitId) ?? unit.unitId,
    subtitle:
      typeof siteId === 'string' ? sources.getSiteName(siteId) : undefined,
    pills: assetPills,
    tickets: [
      headlineTicketView(headline, sources),
      ...earlier.map((ticket) => earlierTicketView(ticket, sources)),
    ],
  }
}

/** One card per chain, sorted by asset name then workflow. */
export function toCardViews(sources: CardSources): CardView[] {
  const assetFields = sources.settings.fields.filter(
    (field) => fieldPillScope(field) === 'asset'
  )
  const cards = sources.units.flatMap((unit) => {
    const assetPills = assetFields.flatMap((field) =>
      fieldPills(field, unit.statsTicket)
    )
    return compact(
      unit.chains.map((chain) => chainToCard(unit, chain, assetPills, sources))
    )
  })
  return cards.sort(
    (a, b) =>
      a.title.localeCompare(b.title) ||
      (a.tickets[0]?.title ?? '').localeCompare(b.tickets[0]?.title ?? '')
  )
}
